import cx from 'classnames'
import Avatar from 'components/Avatar'
import LoadingOverlay from 'components/LoadingOverlay'
import Text from 'components/Text'
import { useElementSize } from 'hooks/element-size'
import { useLoading } from 'hooks/loading-hooks'
import { observer } from 'mobx-react'
import * as React from 'react'
import Summary from '../../../../Summary'
import SubmissionViewModel, { LegendInfo } from '../../../SubmissionViewModel'
import styles from './Summary.sass'

interface SummaryProps {
  vm: SubmissionViewModel
}

/**
 * Breakpoint for the element width.
 * If it goes below, use the vertical layout.
 */
const VERTICAL_LAYOUT_BREAKPOINT = 680

/**
 * The Summary view.
 */
export default observer(function Summary({ vm }: SummaryProps) {
  const rootRef = React.useRef(null)
  const size = useElementSize(rootRef)
  const hasSummary = Boolean(vm.summary?.items.length)
  // Use vertical layout when space becomes limited
  // or if showing less than 3 items. The horizontal layout
  // with 2 or less items looks terrible.
  const useVerticalLayout =
    size.width <= VERTICAL_LAYOUT_BREAKPOINT ||
    (vm.summary?.items.length || 0) < 3

  // Ensures we only show the loader if loading for more than a perceivable
  // duration, but only for subsequent loads.
  const showingLoader = useLoading(vm.isLoading)
  return (
    <div
      ref={rootRef}
      className={cx(
        styles.root,
        useVerticalLayout && styles.vertical,
        hasSummary && styles.hasSummary
      )}
    >
      {vm.legendInfo && <LegendInfo legendInfo={vm.legendInfo} />}
      {hasSummary && <Info summary={vm.summary!} />}
      {showingLoader && <LoadingOverlay data-test="summary-loading-overlay" />}
    </div>
  )
})

interface LegendInfoProps {
  legendInfo: LegendInfo
}

/**
 * Legend info.
 */
function LegendInfo({ legendInfo }: LegendInfoProps) {
  return (
    <div className={styles.legend}>
      <div className={styles.legendContent}>
        <div className={styles.legendVisual}>
          <Avatar
            size={64}
            subject={{
              displayName: legendInfo.title,
              avatar: legendInfo.imageUrl,
            }}
          />
        </div>
        <div className={styles.legendTitle}>{legendInfo.title}</div>
      </div>
    </div>
  )
}

interface InfoProps {
  summary: Summary
}

/**
 " Info
 */
export function Info({ summary }: InfoProps) {
  return (
    <div className={styles.info}>
      <div className={styles.infoContent}>
        {summary.items.map((item, i) => {
          switch (item.type) {
            case 'Divider':
              return <div key={i} className={styles.line} />
            case 'Item':
              return (
                <Item
                  key={i}
                  highlighted={item.highlighted}
                  label={item.label}
                  value={item.value}
                  data-test={`summary-item-${item.label}`}
                />
              )
          }

          return null
        })}
      </div>
    </div>
  )
}

interface ItemProps {
  label: string
  value: string
  highlighted?: boolean
  ['data-test']?: string
}

/**
 " Item
 */
export function Item({
  label,
  value,
  highlighted,
  'data-test': dataTest,
}: ItemProps) {
  return (
    <div className={cx(styles.item, highlighted && styles.highlighted)}>
      <div className={styles.itemLabel} data-test={`${dataTest}-label`}>
        {label}
      </div>
      <div className={styles.itemValue} data-test={`${dataTest}-value`}>
        <Text text={value} />
      </div>
    </div>
  )
}
